import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircle, CheckCircle2, Lock, Shield, ShieldAlert, Zap } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardBody } from '../components/ui/Card';
import { PasswordStrengthIndicator } from '../components/security/PasswordStrengthIndicator';
import { useChangePassword } from '../hooks/useUserSecurity';
import { useAuthStore } from '../stores/auth.store';
import { clearPasswordResetFlag } from '../lib/passwordResetFlag';

export function ForcedPasswordReset() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isInitialized, logout } = useAuthStore();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const changePassword = useChangePassword();

  // Block any attempt to navigate away via the browser back button until reset
  useEffect(() => {
    const handlePopState = () => {
      window.history.pushState(null, '', window.location.pathname);
    };
    window.history.pushState(null, '', window.location.pathname);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // If the user somehow lands here while logged out, send them back to login
  useEffect(() => {
    if (isInitialized && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, isInitialized, navigate]);

  const canSubmit =
    !!currentPassword &&
    !!newPassword &&
    isValid &&
    newPassword === confirmPassword &&
    !changePassword.isPending &&
    !success;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('As senhas não conferem.');
      return;
    }
    if (!isValid) {
      setError('A nova senha não atende aos requisitos mínimos.');
      return;
    }
    if (currentPassword === newPassword) {
      setError('A nova senha deve ser diferente da atual.');
      return;
    }
    setError(null);
    try {
      await changePassword.mutateAsync({ currentPassword, newPassword });
      clearPasswordResetFlag();
      setSuccess(true);
      // Backend revokes tokens — logout and redirect to login after short delay
      setTimeout(() => {
        logout();
        window.location.href = '/login';
      }, 2500);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ??
        (err instanceof Error ? err.message : 'Erro ao alterar senha.');
      setError(message);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-surface-950 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-red-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-primary-500/5 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Logo */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-accent-500 mb-4 shadow-glow">
            <Zap className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold gradient-text">
            {import.meta.env.VITE_APP_NAME || 'Hub Business'}
          </h1>
        </div>

        <Card variant="glass" className="backdrop-blur-xl">
          <CardBody className="p-8">
            {/* Banner */}
            <div className="flex items-start gap-3 p-4 mb-6 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
              <ShieldAlert className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-400">
                  Atualização de senha necessária
                </p>
                <p className="text-xs text-yellow-300/80 mt-1">
                  Por questões de segurança, sua senha precisa ser atualizada
                  antes de continuar usando a plataforma.
                </p>
              </div>
            </div>

            <div className="mb-6">
              <h2 className="text-xl font-bold text-surface-100 flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary-400" />
                Atualize sua senha
              </h2>
              {user && (
                <p className="text-sm text-surface-400 mt-1">
                  Conta: <span className="text-surface-200">{user.email}</span>
                </p>
              )}
            </div>

            {success ? (
              <div className="flex items-start gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/30 text-green-400">
                <CheckCircle2 className="w-6 h-6 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Senha atualizada!</p>
                  <p className="text-xs mt-1 opacity-90">
                    Redirecionando para o login… Faça login novamente com sua
                    nova senha.
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  label="Senha atual"
                  type="password"
                  leftIcon={<Lock className="w-4 h-4" />}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  autoComplete="current-password"
                  autoFocus
                />

                <Input
                  label="Nova senha"
                  type="password"
                  leftIcon={<Lock className="w-4 h-4" />}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                />

                <PasswordStrengthIndicator
                  password={newPassword}
                  onValidChange={setIsValid}
                />

                <Input
                  label="Confirmar nova senha"
                  type="password"
                  leftIcon={<Lock className="w-4 h-4" />}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  error={
                    confirmPassword && newPassword !== confirmPassword
                      ? 'As senhas não conferem'
                      : undefined
                  }
                />

                {error && (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}

                <Button
                  type="submit"
                  variant="primary"
                  className="w-full"
                  isLoading={changePassword.isPending}
                  disabled={!canSubmit}
                >
                  Atualizar senha
                </Button>

                <p className="text-xs text-surface-500 text-center">
                  Ao continuar, suas sessões serão encerradas e você precisará
                  fazer login novamente.
                </p>
              </form>
            )}
          </CardBody>
        </Card>
      </motion.div>
    </div>
  );
}

export default ForcedPasswordReset;
